/**
 * Contrat de l'API analytics — SOURCE DE VÉRITÉ UNIQUE.
 *
 * Ce fichier est importé par le backend ET par le frontend, via l'alias
 * `@shared/*` déclaré dans les deux tsconfig.json. Aucun type de réponse d'API
 * ne doit être redéclaré d'un côté ou de l'autre : si la forme change ici, les
 * deux compilations cassent en même temps, ce qui est exactement le but.
 *
 * Il contient aussi quelques constantes (libellés, bornes de validation)
 * partagées entre le back et le front — d'où un vrai import runtime, et pas
 * seulement des `import type`.
 */

import type { AdminRole } from './auth';

// -----------------------------------------------------------------------------
// Constantes partagées
// -----------------------------------------------------------------------------

/**
 * Fuseau d'agrégation des statistiques.
 *
 * Tout est STOCKÉ en UTC, mais agrégé en heure de Paris : les crons et les KPI
 * du serveur de prod datent déjà leurs journées ainsi. Agréger en UTC donnerait
 * des chiffres justes mais impossibles à rapprocher de l'existant.
 */
export const ANALYTICS_TIMEZONE = 'Europe/Paris';

/**
 * Amplitude maximale d'une période, en jours.
 *
 * Deux ans : au-delà, la requête balaie l'essentiel des ~1,15 M lignes de
 * `user_actions` et le cache de 5 minutes ne sert plus à rien. C'est une borne
 * de garde, pas une limite produit — elle se relève si le besoin se présente.
 */
export const PERIOD_MAX_DAYS = 730;

/** Valeur par défaut et maximum du paramètre `topTracksLimit`. */
export const TOP_TRACKS_DEFAULT_LIMIT = 20;
export const TOP_TRACKS_MAX_LIMIT = 100;

/**
 * Valeurs de l'enum `track_action` côté prod, dans l'ordre de leur ajout.
 *
 * ⚠️ Le backend ne s'en sert PAS pour filtrer : si une valeur est ajoutée en
 * prod, le sync l'aligne automatiquement et elle apparaît dans les réponses
 * même si elle manque ici. Cette liste sert à l'affichage (ordre et libellés) ;
 * le front doit tolérer une action inconnue.
 */
export const ACTION_NAMES = [
  'play',
  'learn',
  'lessons',
  'click',
  'transcription',
  'recording',
  'wifi_connection',
  'brightness',
  'language',
  'setup_flutter',
] as const;

export type ActionName = (typeof ACTION_NAMES)[number];

/** Libellés français des actions, pour les légendes de graphiques. */
export const ACTION_LABELS: Record<ActionName, string> = {
  play: 'Lecture',
  learn: 'Apprentissage',
  lessons: 'Parcours',
  click: 'Navigation',
  transcription: 'Transcription',
  recording: 'Enregistrement',
  wifi_connection: 'Connexion Wi-Fi',
  brightness: 'Luminosité',
  language: 'Langue',
  setup_flutter: 'Installation',
};

/** Libellé d'affichage d'une action, y compris si elle est inconnue de ce fichier. */
export function actionLabel(actionName: string): string {
  return ACTION_LABELS[actionName as ActionName] ?? actionName;
}

/**
 * Segments d'abonnement d'un utilisateur actif.
 *
 * `unknown` n'est pas une erreur : c'est un compte présent dans `user_actions`
 * mais absent de `users_projection`, c'est-à-dire supprimé côté prod depuis. La
 * base analytics n'a pas de FK entre les deux, et la jointure est un LEFT JOIN
 * justement pour ne pas perdre cette activité passée.
 */
export const SUBSCRIPTION_SEGMENTS = ['free', 'premium', 'unknown'] as const;
export type SubscriptionSegment = (typeof SUBSCRIPTION_SEGMENTS)[number];

export const SUBSCRIPTION_LABELS: Record<SubscriptionSegment, string> = {
  free: 'Gratuit',
  premium: 'Abonné',
  unknown: 'Compte supprimé',
};

// -----------------------------------------------------------------------------
// GET /api/stats/period
// -----------------------------------------------------------------------------

/** Paramètres de requête, tels qu'acceptés en query string. */
export interface PeriodQuery {
  /** Premier jour inclus, `YYYY-MM-DD`, en heure de Paris. */
  from: string;
  /** Dernier jour inclus, `YYYY-MM-DD`, en heure de Paris. */
  to: string;
  /** Nombre de morceaux dans `topTracks`. Défaut : 20. */
  topTracksLimit?: number;
}

/** Période effectivement couverte par la réponse. */
export interface PeriodRange {
  from: string;
  to: string;
  /** Nombre de jours inclus (bornes comprises). */
  days: number;
  timezone: string;
}

export interface PeriodSummary {
  /** Nombre total de lignes `user_actions` sur la période. */
  totalActions: number;
  /** Comptes distincts ayant produit au moins une action. */
  activeUsers: number;
  /** Comptes créés pendant la période (source : `users_projection.created_at`). */
  newAccounts: number;
  /**
   * Temps de pratique cumulé, en secondes.
   *
   * Somme de `action_duration`, que l'app renseigne en secondes pour les
   * actions `learn`, `lessons`, `transcription` et `setup_flutter`. Les autres
   * actions valent 0 — c'est donc un temps de pratique, pas un temps d'usage.
   */
  practiceSeconds: number;
  /** `totalActions / activeUsers`, arrondi à une décimale. 0 si aucun actif. */
  avgActionsPerActiveUser: number;
}

// -----------------------------------------------------------------------------
// Pénétration : la période, la précédente, et la même un an avant
// -----------------------------------------------------------------------------

/**
 * Pénétration des comptes créés dans une fenêtre.
 *
 * ── Le dénominateur est borné des DEUX côtés ────────────────────────────────
 * Ce sont les comptes dont la date de création tombe dans la fenêtre, et non
 * tous les comptes existants à sa date de fin. `daily_snapshots.total_accounts`
 * est un cumul depuis le premier jour du produit : un taux calculé dessus ne
 * dépend que de la borne haute, et déplacer la date de début n'y change rien.
 *
 * ── Le numérateur est l'état COURANT ────────────────────────────────────────
 * Parmi ces comptes, ceux qui sont abonnés AUJOURD'HUI
 * (`subscription_status = 'premium'`). C'est la définition de la pénétration,
 * reprise du KPI de prod — à ne pas confondre avec la conversion de cohorte de
 * la section Abonnement, qui compte `ever_subscribed` et inclut les résiliés.
 */
export interface PenetrationReference {
  /** Bornes de la fenêtre, `YYYY-MM-DD`. */
  from: string;
  to: string;
  /** Abonnés actuels rapportés aux comptes créés dans la fenêtre, en %. */
  percent: number;
  /** Comptes CRÉÉS dans la fenêtre. */
  totalAccounts: number;
  /** Parmi eux, ceux actuellement abonnés. */
  totalSubscribers: number;
}

/**
 * Pénétration de la période, comparée à deux fenêtres de référence.
 *
 * ── ⚠️ Un taux de cohorte MÛRIT ─────────────────────────────────────────────
 * Un compte créé avant-hier n'a pas encore eu le temps de s'abonner. Sur une
 * fenêtre récente et courte, ce taux est donc structurellement plus bas que
 * celui d'une fenêtre ancienne : ce n'est pas une dégradation, c'est un manque
 * de recul. Le délai médian de conversion, sur la section Abonnement, dit
 * combien de temps il faut attendre avant qu'une fenêtre soit jugeable.
 *
 * Conséquence directe : `previous` en souffre (deux fenêtres d'âges
 * différents), `yearAgo` non (deux fenêtres de même âge relatif). C'est la
 * comparaison la plus fiable des deux, et de loin.
 *
 * Une fenêtre sans aucun compte créé rend `null` — pas un taux de 0 %.
 *
 * ── Deux fenêtres de comparaison ────────────────────────────────────────────
 *  · `previous` — la période immédiatement antérieure, de MÊME longueur. Elle
 *    répond à « est-ce que ça progresse ? » mais elle est sensible à la
 *    saisonnalité : comparer décembre à novembre mélange l'effet des fêtes.
 *  · `yearAgo` — la même période un an avant, calculée en INTERVAL '1 year'
 *    (donc juste sur une année bissextile). Elle neutralise la saisonnalité,
 *    au prix d'un écart qui mêle douze mois de changements.
 *
 * Les deux ensemble, parce qu'aucune ne suffit seule.
 *
 * ── Points ET pourcentage relatif ───────────────────────────────────────────
 * Les deux sont publiés, et il faut savoir lequel on lit. Passer de 21 % à
 * 22,2 % c'est **+1,2 point** ou **+5,7 % en relatif**. Le point est la mesure
 * juste de l'écart d'un taux ; le relatif rend deux périodes comparables entre
 * elles. Le front affiche le relatif et garde les points à côté, parce que
 * « +5,7 % » tout seul se fait lire comme 5,7 points par la moitié des
 * lecteurs.
 */
export interface PenetrationComparison {
  /** Comptes créés dans la période affichée. */
  current: PenetrationReference | null;
  /** Comptes créés dans la période précédente, de même longueur. */
  previous: PenetrationReference | null;
  /** Comptes créés dans la même période un an avant. */
  yearAgo: PenetrationReference | null;

  /** Variation relative contre `previous`, en %. `null` si une borne manque. */
  periodOverPeriodPercent: number | null;
  /** Écart contre `previous`, en POINTS. */
  periodOverPeriodPoints: number | null;

  /** Variation relative contre `yearAgo`, en %. */
  yearOverYearPercent: number | null;
  /** Écart contre `yearAgo`, en POINTS. */
  yearOverYearPoints: number | null;

  /**
   * Les deux fenêtres de comparaison désignent-elles la même tranche ?
   *
   * Dès que la période sélectionnée atteint un an, « la période précédente » et
   * « la même période un an avant » se confondent — sur 365 jours elles sont
   * rigoureusement identiques. Les deux écarts sont alors le même nombre, et
   * les afficher côte à côte laisse croire à deux mesures indépendantes qui se
   * confirment l'une l'autre.
   *
   * Le front n'en affiche qu'une seule quand ce drapeau est levé, et dit
   * pourquoi.
   */
  comparisonsOverlap: boolean;
}

/** Un point de la série quotidienne d'activité. Série SANS trous : les jours sans activité valent 0. */
export interface DailyActivityPoint {
  /** `YYYY-MM-DD`, en heure de Paris. */
  date: string;
  actions: number;
  activeUsers: number;
}

export interface ActionBreakdownItem {
  actionName: string;
  actions: number;
  /** Part du total des actions de la période, en pourcentage arrondi à une décimale. */
  share: number;
}

export interface TopTrackItem {
  trackName: string;
  actions: number;
  plays: number;
  /** `learn` + `lessons` confondus. */
  learns: number;
  uniqueUsers: number;
}

export interface SubscriptionSegmentItem {
  status: SubscriptionSegment;
  activeUsers: number;
  actions: number;
}

/**
 * Un point de la courbe comptes/abonnés, issu de `daily_snapshots`.
 *
 * Série potentiellement TROUÉE, à la différence de `dailyActivity` : un jour
 * absent signifie qu'aucun snapshot n'a été pris ce jour-là (sync en échec), et
 * cette donnée est définitivement perdue — la prod ne conserve aucun historique
 * d'abonnement. Le combler par un zéro serait un mensonge ; le front doit
 * afficher une rupture.
 */
export interface AccountsPoint {
  date: string;
  totalAccounts: number;
  totalSubscribers: number;
  annualSubscribers: number;
  /** Déduit : `totalSubscribers - annualSubscribers`. */
  monthlySubscribers: number;
}

export interface PeriodMeta {
  /** Instant de calcul de la réponse (ISO 8601). */
  generatedAt: string;
  /** `true` si la réponse sort du cache in-memory plutôt que de la base. */
  cached: boolean;
  /** Action la plus récente présente en base (ISO 8601), ou `null` si la table est vide. */
  lastActionAt: string | null;
  /** Dernier sync réussi (ISO 8601), ou `null` si la projection est vide. */
  lastSyncAt: string | null;
}

export interface PeriodStats {
  range: PeriodRange;
  summary: PeriodSummary;
  dailyActivity: DailyActivityPoint[];
  /** Cases non vides seulement — le front complète la grille 7 × 24. */
  heatmap: HeatmapCell[];
  actionBreakdown: ActionBreakdownItem[];
  topTracks: TopTrackItem[];
  subscriptionSegments: SubscriptionSegmentItem[];
  accountsTimeline: AccountsPoint[];
  /** Pénétration de la période, et ses deux comparaisons. */
  penetration: PenetrationComparison;
  meta: PeriodMeta;
}

/** Corps de la réponse 200 de `GET /api/stats/period`. */
export interface PeriodStatsResponse {
  stats: PeriodStats;
}

// -----------------------------------------------------------------------------
// POST /api/auth/login
// -----------------------------------------------------------------------------

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AdminSummary {
  id: number;
  email: string;
  /** Rôle du compte (cf. shared/types/auth.ts) : ce qu'il peut ouvrir. */
  role: AdminRole;
  /** Connexion précédente (ISO 8601), `null` à la toute première. */
  lastLoginAt: string | null;
}

// Les réponses de `POST /api/auth/login` (connexion en plusieurs pas) sont
// dans `auth.ts` : LoginStep. Le jeton de session expire au bout de 24 h.

/** Pas de connexion en attente, porté par un ticket (JWT de 10 min). */
export type JwtStage = 'password_change' | 'mfa_setup' | 'mfa';

/**
 * Contenu du JWT. `sub` est l'id de l'admin.
 *
 * Sans `stage` : une session. Avec : un ticket entre deux pas de la
 * connexion, que les routes protégées refusent.
 */
export interface JwtPayload {
  sub: number;
  email: string;
  stage?: JwtStage;
  /** Époque de session du compte à la connexion (`admins.session_epoch`). */
  ep?: number;
  /**
   * Rôle, relu en base à chaque requête par `authenticate` (jamais signé dans
   * le jeton : changer le rôle d'un compte vaut tout de suite).
   */
  role?: AdminRole;
}

// -----------------------------------------------------------------------------
// GET /health
// -----------------------------------------------------------------------------

export interface HealthResponse {
  status: 'ok' | 'degraded';
  database: 'up' | 'down';
  uptimeSeconds: number;
  lastSyncAt: string | null;
}

// -----------------------------------------------------------------------------
// Erreurs
// -----------------------------------------------------------------------------

/**
 * Forme de TOUTE réponse d'erreur, quel que soit le code HTTP.
 * Reprise de la convention majoritaire du back de prod : `{ error: "..." }`.
 */
export interface ApiError {
  error: string;
}

// =============================================================================
// LOT 1 — sections « Vue d'ensemble », « Apprentissage », « Personnalisation »
// du rapport d'analyse.
// =============================================================================

/**
 * Comptes de test, exclus de TOUTES les statistiques.
 *
 * Ce sont des comptes internes qui faussent tout : ils cumulent des milliers
 * d'actions de recette et gonflent aussi bien le nombre d'utilisateurs actifs
 * que les moyennes d'apprentissage. Filtre appliqué côté SQL, une fois, dans
 * `stats.sql.ts` — jamais recopié requête par requête.
 *
 * Les lignes sans `user_id` (NULL) sont CONSERVÉES : ce sont de vraies actions
 * dont le compte n'a pas été transmis, pas des actions de test.
 */
export const TEST_ACCOUNT_IDS = [1, 2, 3, 4] as const;

/**
 * Bornes de plausibilité d'une durée d'apprentissage, en secondes.
 *
 * Au-delà de 6 h, c'est une session laissée ouverte — l'app envoie la durée
 * écoulée, pas le temps réellement joué. Ces valeurs écraseraient toute
 * moyenne, donc on les écarte des statistiques de durée (les lignes restent
 * comptées ailleurs).
 */
export const LEARN_DURATION_MIN_SECONDS = 1;
export const LEARN_DURATION_MAX_SECONDS = 6 * 3600;

// -----------------------------------------------------------------------------
// Vue d'ensemble — heatmap d'activité
// -----------------------------------------------------------------------------

/**
 * Une case de la heatmap heure × jour de semaine.
 *
 * `weekday` suit ISO : 1 = lundi … 7 = dimanche. `hour` va de 0 à 23. L'heure
 * est locale (Europe/Paris), comme toutes les agrégations.
 */
export interface HeatmapCell {
  weekday: number;
  hour: number;
  actions: number;
}

export const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Lun', 2: 'Mar', 3: 'Mer', 4: 'Jeu', 5: 'Ven', 6: 'Sam', 7: 'Dim',
};

// -----------------------------------------------------------------------------
// Apprentissage
// -----------------------------------------------------------------------------

/**
 * Mapping de `learn_hand`.
 *
 * ⚠️ Le Swagger du tracker de prod annonce « 1, 2 ou 0 » — il est périmé, les
 * données contiennent 1, 2 et 3. Le mapping retenu est celui du rapport
 * d'analyse, et la distribution le confirme : la main gauche seule est de loin
 * la plus rare (~4 %), ce qui est attendu.
 */
export const HAND_LABELS: Record<number, string> = {
  1: 'Deux mains',
  2: 'Main gauche',
  3: 'Main droite',
};

export interface HandSplitItem {
  hand: number;
  label: string;
  learns: number;
  /** Part des apprentissages dont la main est renseignée, en %. */
  share: number;
}

/** Les quatre options d'apprentissage, dans l'ordre d'affichage. */
export const LEARN_OPTIONS = [
  { key: 'anticipation', label: 'Anticipation' },
  { key: 'error', label: 'Mode fausse note' },
  { key: 'loop', label: 'Mode boucle' },
  { key: 'blackkey', label: 'Mode touche noire' },
] as const;

export type LearnOptionKey = (typeof LEARN_OPTIONS)[number]['key'];

export interface LearnOptionItem {
  key: LearnOptionKey;
  label: string;
  enabled: number;
  disabled: number;
  /** Part d'apprentissages où l'option est active, en %. */
  enabledShare: number;
}

export interface DifficultyItem {
  /** Niveau 1 à 5. */
  difficulty: number;
  learns: number;
  /**
   * Taux d'erreur du niveau : fausses notes / notes jouées, en %.
   *
   * Ratio global du niveau, et non moyenne des taux par apprentissage : une
   * session de 8 notes pèserait autant qu'une session de 2 000.
   */
  errorRatePercent: number;
}

export interface WeeklyDifficultyPoint {
  /** Lundi de la semaine, `YYYY-MM-DD`. */
  week: string;
  avgDifficulty: number;
  learns: number;
}

export interface LearningSummary {
  totalLearns: number;
  uniqueUsers: number;
  /** Moyenne des durées plausibles, en secondes. */
  avgDurationSeconds: number;
  medianDurationSeconds: number;
  /** Difficulté moyenne jouée, sur 5. */
  avgDifficulty: number;
  /** Fausses notes / notes jouées sur toute la période, en %. */
  errorRatePercent: number;
  totalNotesPressed: number;
  totalWrongNotes: number;
}

export interface LearningStats {
  range: PeriodRange;
  summary: LearningSummary;
  hands: HandSplitItem[];
  options: LearnOptionItem[];
  difficulty: DifficultyItem[];
  weeklyDifficulty: WeeklyDifficultyPoint[];
  meta: PeriodMeta;
}

export interface LearningStatsResponse {
  stats: LearningStats;
}

// -----------------------------------------------------------------------------
// Personnalisation
// -----------------------------------------------------------------------------

export const LANGUAGE_LABELS: Record<string, string> = {
  french: 'Français',
  english: 'Anglais',
  german: 'Allemand',
  spanish: 'Espagnol',
  italian: 'Italien',
  none: 'Non renseignée',
};

export function languageLabel(code: string): string {
  return LANGUAGE_LABELS[code] ?? code;
}

export interface BrightnessBucket {
  /** Valeur de luminosité choisie, 1 à 100. */
  value: number;
  changes: number;
}

export interface LanguageItem {
  language: string;
  label: string;
  changes: number;
  /** Part des changements de langue, en %. */
  share: number;
}

export interface PersonalizationSummary {
  /** Utilisateurs actifs sur la période — dénominateur des deux taux ci-dessous. */
  activeUsers: number;

  brightnessUsers: number;
  brightnessUserShare: number;
  brightnessChanges: number;
  /** Valeur la plus fréquemment choisie, `null` si aucun changement. */
  brightnessMode: number | null;
  brightnessMedian: number | null;

  languageUsers: number;
  languageUserShare: number;
  languageChanges: number;
  /** Code de la langue la plus choisie, `null` si aucun changement. */
  topLanguage: string | null;
}

export interface PersonalizationStats {
  range: PeriodRange;
  summary: PersonalizationSummary;
  brightnessDistribution: BrightnessBucket[];
  languages: LanguageItem[];
  meta: PeriodMeta;
}

export interface PersonalizationStatsResponse {
  stats: PersonalizationStats;
}

// =============================================================================
// LOT 2 — sections « Sessions utilisateurs » et « Catégories & morceaux ».
// =============================================================================

// -----------------------------------------------------------------------------
// Sessions
// -----------------------------------------------------------------------------

/**
 * Écart au-delà duquel deux actions appartiennent à deux sessions.
 *
 * Dix minutes : c'est la convention du rapport d'analyse. Le seuil est
 * arbitraire par nature — il n'existe aucun événement « fin de session » dans
 * le tracker — mais il doit être le MÊME partout, sinon deux chiffres de
 * sessions ne se comparent plus.
 */
export const SESSION_GAP_MINUTES = 10;

export interface SessionSummary {
  totalSessions: number;
  usersWithSessions: number;

  /**
   * Durée d'une session : écart entre sa première et sa dernière action.
   *
   * ⚠️ Une session d'UNE seule action dure donc 0 seconde, alors que la
   * personne a bien joué quelque chose. C'est une sous-estimation structurelle,
   * pas un bug : le tracker n'enregistre pas de fin de session, et la durée de
   * la dernière action n'est connue que pour les apprentissages. La part de ces
   * sessions est donnée par `singleActionShare` — sans elle, la durée médiane
   * se lit de travers.
   */
  avgDurationSeconds: number;
  medianDurationSeconds: number;

  avgActionsPerSession: number;
  medianActionsPerSession: number;
  avgSessionsPerUser: number;

  /** Part des sessions réduites à une seule action, en %. */
  singleActionShare: number;

  /** 99e percentile des durées — borne de l'histogramme. */
  p99DurationSeconds: number;
}

export interface SessionDurationBucket {
  /** Borne basse, incluse, en secondes. */
  fromSeconds: number;
  /** Borne haute, exclue, en secondes. */
  toSeconds: number;
  sessions: number;
}

export interface SessionStats {
  range: PeriodRange;
  summary: SessionSummary;
  durationHistogram: SessionDurationBucket[];
  meta: PeriodMeta;
}

export interface SessionStatsResponse {
  stats: SessionStats;
}

// -----------------------------------------------------------------------------
// Catégories & morceaux
// -----------------------------------------------------------------------------

/**
 * Catégories du catalogue, telles que codées dans le nom de fichier.
 *
 * Convention : `Artiste_Titre-XY.mid`, où X est la difficulté (1-5) et Y la
 * catégorie. Vérifié sur les données : 97,4 % des actions avec un morceau
 * suivent cette convention.
 */
export const TRACK_CATEGORIES = [
  { id: 0, label: 'Classique' },
  { id: 1, label: 'Pop' },
  { id: 2, label: 'Rock' },
  { id: 3, label: 'Film' },
  { id: 4, label: 'Français' },
  { id: 5, label: 'Enfants' },
  { id: 6, label: 'Importation' },
] as const;

/**
 * Deux catégories qui ne viennent pas du nom de fichier.
 *
 * `EXERCISE` : les 28 fichiers `..._major-chord.mid` / `..._major-scale.mid` du
 * Parcours. Ce sont des exercices, pas des morceaux — les verser dans
 * « Importation » gonflerait cette catégorie de 17 500 actions et fausserait
 * toute la répartition.
 *
 * `UNCATALOGUED` : les fichiers que les utilisateurs ont ajoutés eux-mêmes sans
 * suivre la convention (`Für Elise.mid`, `Bella Ciao.mid`…). À ne pas confondre
 * avec la catégorie « Importation » du catalogue, qui est une catégorie
 * officielle.
 */
export const CATEGORY_EXERCISE = -1;
export const CATEGORY_UNCATALOGUED = -2;

export function categoryLabel(id: number | null): string {
  if (id === CATEGORY_EXERCISE) return 'Exercices du Parcours';
  if (id === null || id === CATEGORY_UNCATALOGUED) return 'Hors catalogue';
  return TRACK_CATEGORIES.find((c) => c.id === id)?.label ?? `Catégorie ${id}`;
}

/**
 * Nom de fichier → libellé lisible.
 *
 * `Frederic Chopin_Nocturne Op 9 No 2-10.mid` → `Frederic Chopin — Nocturne Op 9 No 2`.
 * Un nom de fichier brut dans un tableau se lit mal et se compare encore moins
 * bien. Le nom d'origine reste disponible pour qui veut le chercher en base.
 */
export function formatTrackName(trackName: string): string {
  const withoutSuffix = trackName
    .replace(/-(\d)(\d)(\.[a-z0-9]+)?\.mid$/i, '')
    .replace(/\.mid$/i, '');
  const separator = withoutSuffix.indexOf('_');
  if (separator <= 0) return withoutSuffix;
  return `${withoutSuffix.slice(0, separator)} — ${withoutSuffix.slice(separator + 1)}`;
}

export interface CategoryItem {
  categoryId: number;
  label: string;
  plays: number;
  learns: number;
  total: number;
  /** Part du total `play` + `learn` de la période, en %. */
  share: number;
  uniqueUsers: number;
}

export interface CatalogTrackItem {
  trackName: string;
  /** Nom mis en forme pour l'affichage. */
  label: string;
  categoryId: number;
  categoryLabel: string;
  /** Difficulté annoncée par le nom de fichier, `null` si non décodable. */
  difficulty: number | null;
  plays: number;
  learns: number;
  actions: number;
  uniqueUsers: number;
}

export interface CategoryTopTrack {
  categoryId: number;
  categoryLabel: string;
  rank: number;
  trackName: string;
  label: string;
  actions: number;
}

export interface CatalogStats {
  range: PeriodRange;
  categories: CategoryItem[];
  topPlayed: CatalogTrackItem[];
  topLearned: CatalogTrackItem[];
  topByCategory: CategoryTopTrack[];
  meta: PeriodMeta;
}

export interface CatalogStatsResponse {
  stats: CatalogStats;
}

// =============================================================================
// LOT 3 — sections « Rétention & engagement » et « Insights produit ».
// =============================================================================

/**
 * Jalons de rétention, en jours après la première action.
 *
 * Un compte est RETENU à Dn s'il a produit au moins une action à J+n ou après.
 * Il n'est ÉLIGIBLE au calcul que s'il a eu n jours pour revenir : un compte
 * créé hier ne peut pas être « perdu à D30 », et l'inclure au dénominateur
 * ferait plonger le taux mécaniquement à chaque vague d'inscriptions.
 */
export const RETENTION_CHECKPOINTS = [1, 3, 7, 14, 30] as const;

export interface RetentionPoint {
  /** 0 = première action (toujours 100 %), puis 1, 3, 7, 14, 30. */
  day: number;
  eligibleUsers: number;
  retainedUsers: number;
  ratePercent: number;
}

/**
 * Un seau d'un histogramme ordinal.
 *
 * Libellé porté par l'API plutôt que reconstruit côté front : les bornes sont
 * un choix d'analyse, pas de présentation, et elles doivent être identiques
 * dans le graphique et dans le tableau.
 */
export interface LabelledBucket {
  label: string;
  users: number;
}

export interface LifetimeSummary {
  /** Comptes de la cohorte : première action dans la période sélectionnée. */
  cohortUsers: number;

  /**
   * Durée de vie : jours entre la première et la dernière action.
   *
   * Un compte actif un seul jour a donc une durée de vie de 0 — c'est la même
   * convention que pour les sessions, et `singleDayShare` en donne l'ampleur.
   */
  avgLifetimeDays: number;
  medianLifetimeDays: number;

  /** Jours DISTINCTS où le compte a agi — sans rapport avec la durée de vie. */
  avgActiveDays: number;
  medianActiveDays: number;

  /** Part des comptes actifs un seul jour, en %. Le signal d'onboarding. */
  singleDayShare: number;
  /** Part des comptes ayant au moins 7 jours d'activité, en %. */
  sevenPlusDaysShare: number;
}

// -----------------------------------------------------------------------------
// Insights produit
// -----------------------------------------------------------------------------

/**
 * Comparaison personnalisateurs / non-personnalisateurs.
 *
 * Un « personnalisateur » a changé la luminosité ou la langue au moins une fois.
 *
 * ⚠️ C'est une CORRÉLATION. Personnaliser ne rend pas assidu ; c'est sans doute
 * l'inverse — on règle sa lampe quand on a déjà l'intention de rester. Le
 * chiffre est utile comme signal, pas comme levier.
 */
export interface CustomizerComparison {
  customizers: number;
  nonCustomizers: number;
  customizerAvgSessionSeconds: number;
  nonCustomizerAvgSessionSeconds: number;
  customizerAvgActionsPerSession: number;
  nonCustomizerAvgActionsPerSession: number;
}

/**
 * Niveau d'un indicateur au regard d'un seuil de métier.
 *
 * Extrait de `ProductAlert` le jour où les tuiles de KPI ont eu besoin du même
 * vocabulaire : un indicateur au rouge doit se lire pareil qu'il vienne d'une
 * alerte ou d'un seuil de pénétration — mêmes trois niveaux, mêmes icônes,
 * mêmes teintes (`STATUS_COLORS`). Deux vocabulaires concurrents auraient donné
 * deux rouges différents sur la même page.
 */
export type StatusLevel = 'good' | 'warning' | 'critical';

/**
 * Alerte produit calculée automatiquement.
 *
 * Les seuils viennent du rapport d'analyse. Ce sont des repères de métier, pas
 * des vérités : ils sont là pour attirer l'œil, pas pour conclure.
 */
export interface ProductAlert {
  level: StatusLevel;
  /** Ce qui est mesuré, en une ligne. */
  message: string;
  /** La valeur qui a déclenché l'alerte, déjà mise en forme. */
  value: string;
}

// -----------------------------------------------------------------------------
// Comparaison abonnés / non-abonnés
// -----------------------------------------------------------------------------

/**
 * Le segment est défini par `ever_subscribed`, PAS par `subscription_status`.
 *
 * `subscription_status` ne dit que l'état courant : un compte qui s'est abonné
 * pendant huit mois puis a résilié y ressort `free`, et se retrouverait dans le
 * groupe « jamais abonnés » avec toute sa rétention. La comparaison serait
 * faussée par le churn au lieu de le mesurer.
 *
 * ── ⚠️ LA RÉSERVE MAJEURE — à lire avant d'interpréter quoi que ce soit ─────
 *
 * Ces chiffres décrivent un ÉCART, ils ne mesurent pas un EFFET. Deux biais,
 * et le second est le plus vicieux :
 *
 *  1. **Causalité inverse.** Les abonnés reviennent plus parce que les gens
 *     assidus s'abonnent — pas parce que s'abonner rend assidu. Même travers
 *     que la comparaison des personnalisateurs : on règle sa lampe quand on a
 *     déjà l'intention de rester.
 *
 *  2. **Fuite temporelle.** Le segment est lu sur l'état d'AUJOURD'HUI, alors
 *     que la rétention se mesure depuis la première action. Un compte arrivé en
 *     janvier, resté huit mois, abonné en août est étiqueté « abonné » — et les
 *     huit mois de rétention qui ont PRÉCÉDÉ son abonnement sont portés au
 *     crédit du segment. L'étiquette est en partie une conséquence de ce qu'on
 *     mesure, et l'écart en ressort mécaniquement gonflé.
 *
 * C'est pour ça que `medianDaysToSubscribe` est publié à côté : il dit combien
 * de temps s'écoule en moyenne AVANT l'abonnement, donc quelle part de l'écart
 * est tautologique. Une médiane de 45 jours sur une comparaison à J+30 veut
 * dire que la moitié des « abonnés » ne l'étaient pas encore au jalon mesuré.
 */
export const SUBSCRIBER_SEGMENT_LABELS = {
  subscribers: 'Abonnés (au moins une fois)',
  nonSubscribers: 'Jamais abonnés',
} as const;

/** Un taux de rétention pour un segment, à un jalon. */
export interface SegmentRate {
  eligibleUsers: number;
  retainedUsers: number;
  /** `null` si moins de `MIN_GROUP_SIZE` comptes éligibles — un taux sur cinq comptes n'est pas un signal. */
  ratePercent: number | null;
}

export interface SegmentedRetentionPoint {
  /** 0 = première action, puis 1, 3, 7, 14, 30. */
  day: number;
  subscribers: SegmentRate;
  nonSubscribers: SegmentRate;
  /** Écart en POINTS. `null` dès que l'un des deux taux n'est pas publié. */
  gapPoints: number | null;
}

/**
 * Statistiques de COHORTE d'un segment.
 *
 * Population : les comptes dont la première action tombe dans la période,
 * suivis ensuite sur tout l'historique.
 */
export interface SegmentCohortStats {
  users: number;
  avgLifetimeDays: number;
  medianLifetimeDays: number;
  avgActiveDays: number;
  medianActiveDays: number;
  singleDayShare: number;
  sevenPlusDaysShare: number;
}

/**
 * Statistiques d'ACTIVITÉ d'un segment.
 *
 * Population différente de la cohorte, et c'est délibéré : tous les comptes
 * ayant produit une session PENDANT la période, quelle que soit leur date
 * d'arrivée. Les deux répondent à des questions distinctes — « les abonnés
 * reviennent-ils plus ? » et « les abonnés jouent-ils plus longtemps ? » — et
 * les fondre dans un seul tableau ferait comparer des effectifs sans rapport.
 */
export interface SegmentActivityStats {
  users: number;
  sessions: number;
  sessionsPerUser: number;
  avgSessionSeconds: number;
  avgActionsPerSession: number;
}

/**
 * Un seau d'histogramme, avec un effectif par segment.
 *
 * Une seule liste et non deux tableaux parallèles : c'est la forme qu'attend
 * un graphique à barres groupées, et surtout les libellés ne peuvent pas
 * décaler d'un cran entre les deux segments — ils viennent d'une seule source,
 * la définition des seaux.
 */
export interface SegmentedBucket {
  label: string;
  subscribers: number;
  nonSubscribers: number;
}

export interface SubscriberComparison {
  retention: SegmentedRetentionPoint[];

  cohortSubscribers: SegmentCohortStats;
  cohortNonSubscribers: SegmentCohortStats;

  /** Mêmes seaux que les histogrammes globaux, découpés par segment. */
  lifetimeHistogram: SegmentedBucket[];
  activeDaysHistogram: SegmentedBucket[];

  activitySubscribers: SegmentActivityStats;
  activityNonSubscribers: SegmentActivityStats;

  /**
   * Comptes écartés de la comparaison, faute de savoir les classer.
   *
   * Ce sont les comptes supprimés côté prod : leur activité subsiste dans
   * `user_actions`, mais ils n'ont plus de ligne dans `users_projection`. Les
   * verser d'office chez les « jamais abonnés » biaiserait le groupe le plus
   * nombreux. Ils sont donc exclus des deux segments, et comptés ici plutôt
   * que passés sous silence.
   */
  unknownCohortUsers: number;
  unknownActivityUsers: number;

  /**
   * Délai médian entre la première action et le premier abonnement, en jours.
   *
   * La clé de lecture de tout le reste : voir l'en-tête de section. `null` si
   * aucun compte abonné de la cohorte n'a de date d'abonnement.
   */
  medianDaysToSubscribe: number | null;
}

export interface EngagementStats {
  range: PeriodRange;
  retention: RetentionPoint[];
  lifetime: LifetimeSummary;
  lifetimeHistogram: LabelledBucket[];
  activeDaysHistogram: LabelledBucket[];
  customizers: CustomizerComparison;
  subscriberComparison: SubscriberComparison;
  alerts: ProductAlert[];
  meta: PeriodMeta;
}

export interface EngagementStatsResponse {
  stats: EngagementStats;
}

// =============================================================================
// LOT 4 — les cinq analyses explicatives.
//
// Elles partagent trois partis pris :
//
//  · La période définit une COHORTE (comptes dont la première action y tombe),
//    pas une fenêtre d'observation — sauf pour la complétion, qui mesure des
//    apprentissages et non des comptes.
//
//  · Un groupe de moins de 20 comptes n'est pas publié. Un taux calculé sur
//    cinq personnes n'est pas un signal, et l'afficher à côté d'un taux calculé
//    sur trois mille invite à les comparer.
//
//  · Rien n'affirme de causalité. Ces analyses montrent des associations ; le
//    narratif de chaque page le rappelle plutôt que de laisser le lecteur
//    conclure seul.
// =============================================================================

/** En dessous, un groupe n'est pas publié. */
export const MIN_GROUP_SIZE = 20;

// -----------------------------------------------------------------------------
// 8.1 — Cohortes hebdomadaires, rétention EXACTE
// -----------------------------------------------------------------------------

/**
 * Jalons de la rétention exacte.
 *
 * ⚠️ « Exacte » veut dire : le compte a agi CE JOUR-LÀ précisément, pas « ce
 * jour-là ou après ». C'est une mesure beaucoup plus dure que la rétention de
 * l'onglet précédent, et les deux ne se comparent pas. Elle répond à une autre
 * question : « les gens reviennent-ils le lendemain ? », pas « sont-ils encore
 * là une semaine plus tard ? ».
 */
export const EXACT_RETENTION_CHECKPOINTS = [1, 7, 14] as const;

export interface CohortRow {
  /** Lundi de la semaine de cohorte, `YYYY-MM-DD`. Ou 1er du mois si mensuel. */
  cohort: string;
  totalUsers: number;
  eligibleD7: number;
  eligibleD14: number;
  /** Taux de retour EXACT à J+1, J+7, J+14, en %. `null` si groupe trop petit. */
  rateD1: number | null;
  rateD7: number | null;
  rateD14: number | null;
}

export interface CohortAnalysis {
  /** `week` ou `month` — bascule automatique au-delà de 20 semaines. */
  granularity: 'week' | 'month';
  cohorts: CohortRow[];
  /** Cohortes écartées faute d'effectif. */
  skippedCohorts: number;
}

// -----------------------------------------------------------------------------
// 8.2 — Progression individuelle : difficulté S1 contre S4
// -----------------------------------------------------------------------------

/** Seuil de part et d'autre duquel on parle de progression ou de régression. */
export const PROGRESSION_THRESHOLD = 0.1;

export interface ProgressionPoint {
  /** Difficulté moyenne jouée en semaine 1. */
  week1: number;
  /** Difficulté moyenne jouée en semaine 4. */
  week4: number;
}

export interface ProgressionAnalysis {
  eligibleUsers: number;
  avgDelta: number;
  medianDelta: number;
  progressingShare: number;
  stableShare: number;
  regressingShare: number;
  /** Distribution du delta, seaux nommés. */
  deltaHistogram: LabelledBucket[];
  /** Un point par compte éligible, pour le nuage S1 × S4. */
  points: ProgressionPoint[];
}

// -----------------------------------------------------------------------------
// 8.3 — Taux de complétion des apprentissages
// -----------------------------------------------------------------------------

/**
 * Seuils de complétion, en fraction de la médiane du morceau.
 *
 * On ne sait pas si un apprentissage est « fini » : le tracker n'enregistre pas
 * de fin de morceau. On compare donc le nombre de notes jouées à la MÉDIANE
 * observée pour ce morceau — une mesure relative, pas absolue.
 *
 * Conséquence à garder en tête : par construction, environ la moitié des
 * tentatives d'un morceau sont sous sa médiane. Le seuil de 50 % ne dit pas
 * « la moitié des gens finissent », il dit « ils ont joué au moins la moitié de
 * ce qu'on joue habituellement sur ce morceau ».
 */
export const COMPLETION_THRESHOLD = 0.5;
export const ABANDON_THRESHOLD = 0.2;
/** Nombre minimum de tentatives pour qu'un morceau ait une médiane exploitable. */
export const MIN_ATTEMPTS_FOR_MEDIAN = 20;

export interface CompletionByGroup {
  label: string;
  attempts: number;
  completedShare: number;
  abandonedShare: number;
}

export interface AbandonedTrack {
  trackName: string;
  label: string;
  attempts: number;
  abandonedShare: number;
  completedShare: number;
}

export interface CompletionAnalysis {
  analysedAttempts: number;
  completedShare: number;
  abandonedShare: number;
  /** Distribution du ratio notes jouées / médiane du morceau. */
  ratioHistogram: LabelledBucket[];
  byCategory: CompletionByGroup[];
  byDifficulty: CompletionByGroup[];
  mostAbandoned: AbandonedTrack[];
}

// -----------------------------------------------------------------------------
// 8.4 — Comportement des comptes « un seul jour »
// -----------------------------------------------------------------------------

export interface ChurnAnalysis {
  /** Comptes actifs un seul jour, arrivés assez tôt pour être jugés. */
  users: number;
  /** Part de la cohorte éligible, en %. */
  shareOfCohort: number;
  /** Ont-ils essayé le mode apprentissage ? */
  triedLearn: number;
  neverTriedLearn: number;
  /** Part n'ayant produit qu'UNE action — souvent un échec technique. */
  singleActionShare: number;
  medianActions: number;
  medianSessionSeconds: number;
  /** Distribution du nombre d'actions de la session unique. */
  actionsHistogram: LabelledBucket[];
  /** Première action, par fréquence. */
  firstActions: Array<{ actionName: string; users: number; share: number }>;
}

// -----------------------------------------------------------------------------
// 8.5 — Corrélation option activée en semaine 1 → rétention J+14
// -----------------------------------------------------------------------------

export interface FeatureLiftRow {
  key: LearnOptionKey;
  label: string;
  usersWithFeature: number;
  usersWithout: number;
  /** Rétention EXACTE à J+14, en %. `null` si le groupe est trop petit. */
  rateWith: number | null;
  rateWithout: number | null;
  /** Écart en points de pourcentage. `null` si un des deux groupes manque. */
  liftPoints: number | null;
}

export interface FeatureAnalysis {
  eligibleUsers: number;
  features: FeatureLiftRow[];
}

// -----------------------------------------------------------------------------
// Enveloppes de réponse
// -----------------------------------------------------------------------------

export interface AnalysisEnvelope<T> {
  range: PeriodRange;
  analysis: T;
  meta: PeriodMeta;
}

export type CohortAnalysisResponse = { stats: AnalysisEnvelope<CohortAnalysis> };
export type ProgressionAnalysisResponse = { stats: AnalysisEnvelope<ProgressionAnalysis> };
export type CompletionAnalysisResponse = { stats: AnalysisEnvelope<CompletionAnalysis> };
export type ChurnAnalysisResponse = { stats: AnalysisEnvelope<ChurnAnalysis> };
export type FeatureAnalysisResponse = { stats: AnalysisEnvelope<FeatureAnalysis> };

// =============================================================================
// SECTION ABONNEMENT — pénétration et rétention par cohorte.
// =============================================================================

/** Horizons de survie d'un abonnement, en mois après son début. */
export const SUBSCRIPTION_HORIZONS = [1, 3, 6, 12] as const;

export interface PenetrationPoint {
  date: string;
  totalAccounts: number;
  totalSubscribers: number;
  annualSubscribers: number;
  monthlySubscribers: number;
  /** `total_subscribers / total_accounts`, en %. */
  penetrationPercent: number;
}

/**
 * Conversion d'une cohorte d'INSCRIPTION.
 *
 * ⚠️ Piège de lecture : les cohortes anciennes ont eu plus de temps pour
 * convertir ET plus de temps pour résilier. Les deux effets se confondent, et
 * comparer une cohorte de 2025 à une de cette année n'a de sens que si on garde
 * ça en tête. C'est la raison d'être de la courbe de survie plus bas, qui
 * elle-même normalise par l'ancienneté de l'abonnement.
 */
export interface SignupCohortRow {
  /** 1er du mois d'inscription, `YYYY-MM-DD`. */
  cohort: string;
  accounts: number;
  /** Comptes s'étant abonnés au moins une fois. */
  converted: number;
  /** Parmi eux, ceux encore abonnés aujourd'hui. */
  stillSubscribed: number;
  churned: number;
  /** `converted / accounts`, en %. */
  conversionPercent: number;
  /** `stillSubscribed / converted`, en %. `null` si personne n'a converti. */
  retentionPercent: number | null;
}

/**
 * Survie des abonnements d'une cohorte d'ABONNEMENT.
 *
 * Cohorte définie par le mois du premier abonnement, pas de l'inscription :
 * c'est ce qui permet de comparer des abonnements de même âge.
 *
 * Un taux vaut `null` quand moins de 20 abonnements ont eu le temps d'atteindre
 * l'horizon.
 */
export interface SubscriptionCohortRow {
  /** 1er du mois du premier abonnement, `YYYY-MM-DD`. */
  cohort: string;
  subscriptions: number;
  rateM1: number | null;
  rateM3: number | null;
  rateM6: number | null;
  rateM12: number | null;
  /** Abonnements ayant eu le temps d'atteindre chaque horizon. */
  eligibleM1: number;
  eligibleM3: number;
  eligibleM6: number;
  eligibleM12: number;
}

export interface SubscriptionSummary {
  /** Dernier taux de pénétration connu, en %. */
  currentPenetrationPercent: number;
  totalAccounts: number;
  totalSubscribers: number;

  /** Comptes s'étant abonnés au moins une fois, sur la cohorte de la période. */
  convertedUsers: number;
  cohortAccounts: number;
  conversionPercent: number;

  /** Part des convertis encore abonnés. `null` si aucun converti. */
  subscriberRetentionPercent: number | null;

  /** Délai médian entre inscription et premier abonnement, en jours. */
  medianDaysToConvert: number | null;

  /**
   * Comptes marqués `premium` sans ligne Stripe correspondante.
   *
   * Signalé plutôt que masqué : ce sont des abonnements attribués à la main,
   * ou des comptes dont la ligne Stripe a disparu. Ils font que le nombre
   * d'abonnés du KPI peut dépasser le nombre de conversions constatées.
   */
  premiumWithoutSubscriptionRow: number;
}

export interface SubscriptionStats {
  range: PeriodRange;
  summary: SubscriptionSummary;
  penetration: PenetrationPoint[];
  signupCohorts: SignupCohortRow[];
  daysToConvertHistogram: LabelledBucket[];
  subscriptionCohorts: SubscriptionCohortRow[];
  meta: PeriodMeta;
}

export interface SubscriptionStatsResponse {
  stats: SubscriptionStats;
}

// -----------------------------------------------------------------------------
// /api/markers — marqueurs d'évènements
// -----------------------------------------------------------------------------

/**
 * Nature d'un marqueur.
 *
 * Cinq natures et pas plus : la liste sert à colorer et à filtrer, pas à
 * classer finement. Un champ libre supplémentaire (`note`) porte le détail.
 *
 * ⚠️ Toute valeur ajoutée ici doit l'être AUSSI dans la contrainte CHECK de
 * `markers.kind` (migration 002), sinon l'API renvoie 400 sur une valeur que
 * le front propose.
 */
export const MARKER_KINDS = ['release', 'pricing', 'marketing', 'content', 'other'] as const;
export type MarkerKind = (typeof MARKER_KINDS)[number];

export const MARKER_KIND_LABELS: Record<MarkerKind, string> = {
  release: 'Mise à jour',
  pricing: 'Tarif',
  marketing: 'Campagne',
  content: 'Contenu',
  other: 'Autre',
};

/** Bornes de saisie, partagées par la validation Zod et les champs du front. */
/**
 * Où s'affiche un marqueur : graphiques d'ANALYSE (usage de l'app), de VENTES
 * (Shopify, abonnements, Amazon, pub…), ou les DEUX. Même liste que le CHECK
 * `markers_scope_chk` (migration 017).
 */
export const MARKER_SCOPES = ['analytics', 'sales', 'both'] as const;
export type MarkerScope = (typeof MARKER_SCOPES)[number];

export const MARKER_SCOPE_LABELS: Record<MarkerScope, string> = {
  analytics: 'Analyses',
  sales: 'Ventes',
  both: 'Analyses et ventes',
};

/** Le marqueur doit-il s'afficher sur les graphiques de cette famille ? */
export const markerShownIn = (marker: { scope: MarkerScope }, family: 'analytics' | 'sales'): boolean =>
  marker.scope === 'both' || marker.scope === family;

export const MARKER_LABEL_MAX_LENGTH = 80;
export const MARKER_NOTE_MAX_LENGTH = 500;

/**
 * Fenêtre de comparaison avant/après, en jours.
 *
 * Quatorze jours de chaque côté : assez pour absorber le cycle hebdomadaire
 * (l'activité du dashboard est nettement plus faible le week-end, cf. la
 * heatmap), assez court pour qu'un autre changement ne vienne pas se mêler à
 * celui qu'on mesure.
 */
export const MARKER_IMPACT_WINDOW_DAYS = 14;

/**
 * Nombre minimal de jours mesurés de chaque côté pour publier un verdict.
 *
 * En dessous, la moyenne se joue sur deux ou trois points et le moindre jour
 * creux inverse la conclusion. Mieux vaut dire « pas assez de recul ».
 */
export const MARKER_IMPACT_MIN_DAYS = 5;

/**
 * Variation en dessous de laquelle on ne conclut pas, en %.
 *
 * L'activité quotidienne varie de plusieurs pourcents d'une quinzaine à
 * l'autre sans qu'il se soit rien passé. Annoncer « +2 %, bénéfique » serait
 * lire du bruit.
 */
export const MARKER_IMPACT_NEUTRAL_PERCENT = 5;

/** Un marqueur, tel que l'API le renvoie. */
export interface Marker {
  id: number;
  /** Jour de l'évènement, `YYYY-MM-DD`, en heure de Paris. */
  date: string;
  label: string;
  kind: MarkerKind;
  scope: MarkerScope;
  /** Détail libre, ou `null`. */
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Corps accepté en création et en modification. */
export interface MarkerInput {
  date: string;
  label: string;
  kind: MarkerKind;
  /** Absent : « analytics ». */
  scope?: MarkerScope;
  note?: string | null;
}

export interface MarkerListResponse {
  markers: Marker[];
}

export interface MarkerResponse {
  marker: Marker;
}

// -----------------------------------------------------------------------------
// Seuils du taux de pénétration
// -----------------------------------------------------------------------------

/**
 * Bornes de lecture du taux de pénétration, en %.
 *
 * Objectifs de MÉTIER, arbitrés par l'équipe — pas des valeurs mesurées ni
 * calculées. Ils vivent ici plutôt que dans le composant pour que le back
 * puisse un jour en tirer une alerte sans que les deux jeux de seuils
 * divergent.
 *
 * Partagés par la vue d'ensemble ; à relever ou abaisser ici, en un seul
 * endroit.
 */
export const PENETRATION_THRESHOLDS = {
  /** En dessous : rouge. */
  warning: 35,
  /** Strictement au-dessus : vert. Entre les deux bornes, incluses : orange. */
  good: 40,
} as const;

/**
 * Niveau d'un taux de pénétration au regard des seuils.
 *
 * Bornes INCLUSES dans la zone orange, des deux côtés : la règle est « sous
 * 35 % rouge, entre 35 et 40 orange, au-dessus de 40 vert ». Exactement 35 et
 * exactement 40 sont donc orange — un seuil franchi doit l'être vraiment, pas
 * par l'arrondi de la deuxième décimale.
 */
export function penetrationStatus(percent: number): StatusLevel {
  if (percent < PENETRATION_THRESHOLDS.warning) return 'critical';
  if (percent > PENETRATION_THRESHOLDS.good) return 'good';
  return 'warning';
}

/** Libellé du niveau, qui énonce la règle plutôt que de la sous-entendre. */
export function penetrationStatusLabel(level: StatusLevel): string {
  switch (level) {
    case 'critical':
      return `sous l'objectif de ${PENETRATION_THRESHOLDS.warning} %`;
    case 'warning':
      return `entre ${PENETRATION_THRESHOLDS.warning} et ${PENETRATION_THRESHOLDS.good} %`;
    case 'good':
      return `au-dessus de ${PENETRATION_THRESHOLDS.good} %`;
  }
}
